import { useState, useMemo, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import Pagination from "@mui/material/Pagination";
import {
  FiCalendar,
  FiClock,
  FiZap,
  FiTrendingUp,
  FiCheckCircle,
  FiXCircle,
  FiChevronDown,
  FiChevronUp,
  FiChevronLeft,
  FiChevronRight,
  FiServer,
  FiActivity,
  FiRefreshCw,
  FiPower,
  FiSun,
  FiFilter,
  FiAlertCircle,
  FiLoader,
  FiX,
  FiFileText,
  FiThermometer,
  FiWind,
  FiSlash,
  FiRewind,
} from "react-icons/fi";
import { SCHEDULE_HISTORY_API } from "../api/apiroutes";
import { FaMastodon, FaStar } from "react-icons/fa";

export default function ScheduleHistory() {
  const token = useSelector((state) => state.user.token);
  const navigate = useNavigate();

  const [viewMode, setViewMode] = useState("monthly"); // "weekly" | "monthly"

  // Date range state - Default to last 7 days (previous 7 days from today)
  const [startDate, setStartDate] = useState(() => {
    const today = new Date();
    const startDate = new Date(today);
    startDate.setDate(today.getDate() - 7); // 7 days before today
    // Use local date string instead of toISOString
    const year = startDate.getFullYear();
    const month = String(startDate.getMonth() + 1).padStart(2, "0");
    const day = String(startDate.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  });
  const [endDate, setEndDate] = useState(() => {
    const today = new Date();
    // Use local date string instead of toISOString
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`; // Today as end date
  });

  // Data and UI state
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  });

  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dateError, setDateError] = useState("");
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [tempStartDate, setTempStartDate] = useState(startDate);
  const [tempEndDate, setTempEndDate] = useState(endDate);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [statusFilter, setStatusFilter] = useState("all"); // "all" | "success" | "failed"
  const [hoveredUser, setHoveredUser] = useState(null);
const [userDetails, setUserDetails] = useState(null);
const [userLoading, setUserLoading] = useState(false);
const [popupPos, setPopupPos] = useState({ x: 0, y: 0 });

  // Helper: Format date to YYYY-MM-DD
  const formatDateString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };
const fetchUserDetails = async (userId) => {
  if (!userId) return;

  try {
    setUserLoading(true);

    const res = await axios.post(
      `${process.env.REACT_APP_API_BASE_URL}/api/admin/user-stats`,
      { userId },
      { headers: { Authorization: `Bearer ${token}` } }
    );

    setUserDetails(res.data?.data || null);

  } catch (err) {
    console.error("User fetch error", err);
  } finally {
    setUserLoading(false);
  }
};

const handleUserHover = (userId, e) => {
  setHoveredUser(userId);
  setPopupPos({
    x: e.clientX,
    y: e.clientY,
  });
  fetchUserDetails(userId);
};;

const handleUserLeave = () => {
  setHoveredUser(null);
  setUserDetails(null);
};
  // Helper function: Get date range for mode
  const getRangeForMode = (mode, baseDate = new Date()) => {
    const base = new Date(baseDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (mode === "weekly") {
      const start = new Date(
        base.getFullYear(),
        base.getMonth(),
        base.getDate()
      );
      const end = new Date(start);
      end.setDate(start.getDate() + 6);


      // Clamp end date to today if it's in the future
      const effectiveEnd = end > today ? today : end;

      return {
        startStr: formatDateString(start),
        endStr: formatDateString(effectiveEnd),
      };
    }

    // Monthly view – 31 days inclusive
    const start = new Date(base.getFullYear(), base.getMonth(), 1);
    const end = new Date(base.getFullYear(), base.getMonth() + 1, 0);

    // Clamp end date to today if it's in the future
    const effectiveEnd = end > today ? today : end;

    return {
      startStr: formatDateString(start),
      endStr: formatDateString(effectiveEnd),
    };
  };

   const formatDisplay = (iso, showHebrew = false) => {

      if (!iso) return "";
      const date = new Date(iso);
      const userTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      // const gregorian = dayjs(date).tz
      //   ? dayjs(date).tz(userTz).format("MMM DD, YYYY • hh:mm A")
      //   : dayjs(date).format("MMM DD, YYYY • hh:mm A");
      if (!showHebrew) return "";
      const hebrewDate = new Intl.DateTimeFormat("en-u-ca-hebrew", {
        day: "numeric",
        month: "long",
        year: "numeric",
        timeZone: userTz,
      }).format(date);
      // return `${gregorian} (${hebrewDate})`;
       return `[${hebrewDate}]`;
    };

  const applyCurrentRange = (mode = viewMode) => {
    const now = new Date();

    // For weekly mode, we want "This Week" to be relevant to today.
    // If we want "Last 7 days" style or "Week starting some day".
    // The previous init code used "today - 7".
    // Let's stick to a standard week or just 7 days ending today?
    // Using `getRangeForMode` it treats base as start.
    // If we want 7 days ending today, start should be today-6.

    if (mode === "weekly") {
      // Let's set the start date to 6 days ago so the range ends today.
      // This matches the "Current Week" ending today.
      const startOfWeek = new Date(now);
      startOfWeek.setDate(now.getDate() - 6);

      setCurrentMonth(now); // Keep current month focused on today

      const { startStr, endStr } = getRangeForMode(mode, startOfWeek);
      setStartDate(startStr);
      setEndDate(endStr);
      setDateError("");
      return;
    }

    // Monthly
    now.setDate(1);
    setCurrentMonth(now);

    const { startStr, endStr } = getRangeForMode(mode, now);
    setStartDate(startStr);
    setEndDate(endStr);
    setDateError("");
  };

  const shiftRange = (direction) => {
    let base;

    if (viewMode === "weekly") {
      // For weekly, shift relative to current startDate
      base = new Date(startDate);
      base.setDate(base.getDate() + (direction === "prev" ? -7 : 7));
    } else {
      // For monthly, shift relative to current month view
      base = new Date(currentMonth);
      base.setDate(1); // Ensure 1st of month
      base.setMonth(base.getMonth() + (direction === "prev" ? -1 : 1));
    }

    // Update the control month to follow the view
    setCurrentMonth(new Date(base));

    const { startStr, endStr } = getRangeForMode(viewMode, base);
    setStartDate(startStr);
    setEndDate(endStr);
    setDateError("");
  };

  const getDaysDifference = (start, end) => {
    const s = new Date(start);
    const e = new Date(end);
    s.setHours(0, 0, 0, 0);
    e.setHours(0, 0, 0, 0);
    return Math.floor((e - s) / 86400000) + 1;
  };

  // Validate date range
  const validateDateRange = (start, end) => {
    if (!start || !end) {
      setDateError("Please select both start and end dates");
      return false;
    }

    if (new Date(start) > new Date(end)) {
      setDateError("Start date must be before end date");
      return false;
    }

    const days = getDaysDifference(start, end);

    if (days > 31) {
      setDateError("Date range cannot exceed 31 days");
      return false;
    }

    setDateError("");
    return true;
  };

  // Fetch history logs from API
  const fetchHistoryLogs = useCallback(async (pageNumber = page) => {
    if (!validateDateRange(startDate, endDate)) return;

    if (!token) {
      setError("Authentication token not found. Please login again.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const requestBody = {
        page: pageNumber,
        limit,
        startDate,
        endDate,
      };

      // Add filters if status filter is not "all"
      if (statusFilter !== "all") {
        requestBody.filters = {
          cronstatus: statusFilter,
        };
      }

      const response = await axios.post(
        SCHEDULE_HISTORY_API.SEARCH,
        requestBody,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "x-client-type": "web",
            "Content-Type": "application/json",
          },
        }
      );

      const result = response.data;
     
      // Log only essential primitive data to avoid circular reference errors
    

      if (result?.code === 200) {
        setData(result.data || []);
        // If API returns pagination, use it; otherwise calculate
        if (result.pagination) {
          setPagination(result.pagination);
          setPage(result.pagination.page);
        } else {
          // Calculate pagination from data length
          const totalPages = Math.ceil((result.data?.length || 0) / limit);
          setPagination({
            page: pageNumber,
            limit,
            total: result.data?.length || 0,
            totalPages: totalPages || 1,
          });
        }
      } else {
        setData([]);
      }
    } catch (err) {
      // Log only safe error properties to avoid circular reference errors
      try {
        console.error("Fetch history logs error:", {
          message: err?.message || "Unknown error",
          status: err?.response?.status,
          statusText: err?.response?.statusText,
          errorMessage: err?.response?.data?.message,
        });
      } catch (logError) {
        // Silently ignore logging errors to prevent breaking the app
        console.warn("Could not log error details");
      }

      setError(
        err?.response?.data?.message ||
        err?.message ||
        "Failed to fetch history logs. Please try again."
      );
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, statusFilter, token, limit]);

  // Load data on mount and when date range or status filter changes
  useEffect(() => {
    if (startDate && endDate && token) {
      setPage(1);
      const timer = setTimeout(() => {
        fetchHistoryLogs(1);
      }, 300); // Debounce API calls

      return () => clearTimeout(timer);
    }
  }, [startDate, endDate, statusFilter, token]);

  // Table columns definition
  const columns = [

    {
      header: "Schedule Name",
      cell: ({ row }) => {
        const scheduleName = row.original.scheduleName || "";
        return (
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-purple-100 rounded-md">
              <FiCalendar className="text-purple-600" size={14} />
            </div>
      <span className="font-medium text-gray-700 text-sm flex items-center gap-2">
 
  {row.original.smartRestoreTriggered && (
    <span className="flex items-center gap-1.5 bg-gradient-to-r from-pink-500 to-indigo-500 text-white px-2.5 py-0.5 rounded-full text-xs font-semibold shadow-sm">
      
      <span className="text-sm leading-none">✨</span>
      
      SR
    </span>
  )} 
  


<div
  className="relative inline-block"
  onMouseEnter={(e) => {handleUserHover(row.original.userId, e)}}
  onMouseLeave={handleUserLeave}
>
  <span>{row.original.scheduleName}</span>

  {hoveredUser === row.original.userId && (
    <div className="absolute left-full top-0 ml-3 w-[320px] z-50">
      {/* popup content */}
    </div>
  )}
</div>
<span style={{ color: "#666", fontSize: "13px" }}>
  {formatDisplay(
    row.original?.lastRunAt,
    row.original?.when?.calenderType?.toLowerCase() == "hebrew"?true:false
  )}
</span>

</span>
          </div>
        );
      },
    }
    
    ,
    {
      header: "Status",
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <span
            className={`px-3 py-1.5 text-xs font-medium rounded-full ${row.original.cronstatus === "success"
              ? "bg-emerald-100 text-emerald-700 border border-emerald-200"
              : "bg-red-100 text-red-700 border border-red-200"
              }`}
          >
            {row.original.cronstatus === "success" ? (
              <span className="flex items-center gap-1">
                <FiCheckCircle size={12} /> Success
              </span>
            ) : (
              <span className="flex items-center gap-1">
                <FiXCircle size={12} /> Failed
              </span>
            )}
          </span>
        </div>
      ),
    },
    {
      header: "Schedule Type",
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-indigo-100 rounded-md">
            <FiClock className="text-indigo-600" size={14} />
          </div>
          <span className="font-medium text-gray-700 capitalize">
            {row.original.when?.scheduleType || "N/A"}
          </span>
        </div>
      ),
    },
    {
      header: "Devices",
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-indigo-100 rounded-md">
            <FiServer className="text-indigo-600" size={14} />
          </div>
          <span className="font-semibold text-gray-900">
            {Number(row.original.actions?.length) || 0}
          </span>
          <span className="text-xs text-gray-500">
            device{Number(row.original.actions?.length) !== 1 ? "s" : ""}
          </span>
        </div>
      ),
    },
    {
      header: "Last Run",
      cell: ({ row }) => {
        const lastRunAt = row.original.lastRunAt;
        if (!lastRunAt) return <span className="text-gray-400">N/A</span>;

        return (
          <div className="text-sm">
            <p className="font-medium text-gray-900">
              {new Date(lastRunAt).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              })}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">
              {new Date(lastRunAt).toLocaleTimeString("en-US", {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </p>
          </div>
        );
      },
    },
    {
      header: "Actions",
      cell: ({ row }) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((prev) => ({ ...prev, [row.id]: !prev[row.id] }));
          }}
          className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200 flex items-center gap-1.5 text-sm font-medium text-gray-700"
        >
          {expanded[row.id] ? (
            <>
              <FiChevronUp size={16} /> Hide
            </>
          ) : (
            <>
              <FiChevronDown size={16} /> Details
            </>
          )}
        </button>
      ),
    },
  ];

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const daysSelected =
    startDate && endDate ? getDaysDifference(startDate, endDate) : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header Section */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Schedule History Logs
              </h1>
              <p className="text-gray-600">
                View and monitor schedule execution history and logs
              </p>
            </div>
            <div className="p-4 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl">
              <FiActivity className="text-white" size={28} />
            </div>
          </div>

          {/* Date Range Picker */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="flex flex-wrap items-end gap-4">
              <div className="flex items-center gap-3">
                <FiFilter className="text-gray-600" size={20} />
                <h3 className="text-lg font-semibold text-gray-900">
                  Date Range Filter
                </h3>
              </div>

              {/* Custom Calendar Date Picker Button */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    setTempStartDate(startDate);
                    setTempEndDate(endDate);
                    setCurrentMonth(new Date(startDate || new Date()));
                    setShowDatePicker(true);
                  }}
                  className="px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 flex items-center gap-2 font-medium shadow-md hover:shadow-lg"
                >
                  <FiCalendar size={18} />
                  Select Date Range
                </button>
                {startDate && endDate && (
                  <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm">
                    <span className="font-medium text-gray-900">
                      {new Date(startDate).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                      })}{" "}
                      -{" "}
                      {new Date(endDate).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </span>
                  </div>
                )}
              </div>

              {/* View mode toggle + quick navigation */}
              <div className="flex items-center gap-3 ml-auto">
                <div className="flex items-center bg-gray-100 rounded-full p-1 border border-gray-200 text-xs">
                  {["weekly", "monthly"].map((mode) => (
                    <button
                      key={mode}
                      onClick={() => {
                        setViewMode(mode);
                        applyCurrentRange(mode);
                      }}
                      className={`px-3 py-1 rounded-full font-medium transition-colors ${viewMode === mode
                        ? "bg-white text-blue-700 shadow-sm"
                        : "text-gray-600 hover:text-gray-900"
                        }`}
                    >
                      {mode === "weekly" ? "Weekly" : "Monthly"}
                    </button>
                  ))}
                </div>

                <div className="flex items-center gap-2 text-xs">
                  <button
                    type="button"
                    onClick={() => shiftRange("prev")}
                    className="flex items-center gap-1 px-2 py-1 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100"
                  >
                    <FiChevronLeft size={14} />
                    <span>Prev</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => applyCurrentRange()}
                    className="px-3 py-1 rounded-lg bg-gray-100 text-gray-800 hover:bg-gray-200 font-medium"
                  >
                    This {viewMode === "weekly" ? "Week" : "Month"}
                  </button>
                  <button
                    type="button"
                    onClick={() => shiftRange("next")}
                    disabled={(() => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);

                      // For weekly view, allow navigating until the week that contains today.
                      // Next is disabled only when the *start* of the current range
                      // is within the last 7 days (i.e., we're already on the last week).
                      if (viewMode === "weekly") {
                        const start = new Date(startDate);
                        start.setHours(0, 0, 0, 0);
                        const maxStart = new Date(today);
                        maxStart.setDate(today.getDate() - 6); // start date of the last week window
                        return start >= maxStart;
                      }

                      // For monthly view, disable when the end date reaches or passes today
                      const end = new Date(endDate);
                      end.setHours(0, 0, 0, 0);
                      return end >= today;
                    })()}
                    className="flex items-center gap-1 px-2 py-1 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span>Next</span>
                    <FiChevronRight size={14} />
                  </button>
                </div>
              </div>

              {/* Refresh button */}
              <div className="flex items-end">
                <button
                  onClick={() => fetchHistoryLogs(page)}
                  disabled={loading || !!dateError}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors duration-200 flex items-center gap-2 font-medium"
                >
                  {loading ? (
                    <>
                      <FiLoader className="animate-spin" size={16} />
                     
                    </>
                  ) : (
                    <>
                      <FiRefreshCw size={16} />
                 
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Date Range Info and Error */}
            <div className="mt-4 flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-4 text-sm">
                {startDate && endDate && !dateError && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <FiCalendar size={16} />
                    <span>
                      Selected:{" "}
                      <span className="font-semibold">{daysSelected}</span> day
                      {daysSelected !== 1 ? "s" : ""}({startDate} to {endDate})
                    </span>
                  </div>
                )}
              </div>

              {dateError && (
                <div className="flex items-center gap-2 text-red-600 text-sm">
                  <FiAlertCircle size={16} />
                  <span>{dateError}</span>
                </div>
              )}
            </div>

            {/* Error Message */}
            {error && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
                <FiAlertCircle className="text-red-600" size={20} />
                <div>
                  <p className="font-semibold text-red-900">Error</p>
                  <p className="text-sm text-red-700">{error}</p>
                </div>
                <button
                  onClick={() => setError(null)}
                  className="ml-auto text-red-600 hover:text-red-800"
                >
                  <FiXCircle size={20} />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Custom Date Range Picker Modal */}
        {showDatePicker && (
          <CustomDateRangePicker
            startDate={tempStartDate}
            endDate={tempEndDate}
            currentMonth={currentMonth}
            onStartDateChange={setTempStartDate}
            onEndDateChange={setTempEndDate}
            onMonthChange={setCurrentMonth}
            onClose={() => setShowDatePicker(false)}
            onApply={() => {
              const days = getDaysDifference(tempStartDate, tempEndDate);
              if (days > 31) {
                setDateError("Date range cannot exceed 31 days");
                return;
              }
              setStartDate(tempStartDate);
              setEndDate(tempEndDate);
              setDateError("");
              setShowDatePicker(false);
            }}
          />
        )}

        {/* Main Table Card */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                  <FiZap className="text-yellow-500" size={20} />
                  History Logs
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  {loading
                    ? "Loading history logs..."
                    : `Showing ${data.length} log${data.length !== 1 ? "s" : ""
                    }`}
                </p>
              </div>
              <div className="flex items-center gap-4">
                {/* Status Filter Dropdown */}
                <div className="flex items-center gap-2">
                  <FiFilter className="text-gray-500" size={16} />
                  <label className="text-sm text-gray-600 font-medium">
                    Filter:
                  </label>
                  <select
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                      setPage(1);
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-lg bg-white text-sm font-medium text-gray-700 hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 cursor-pointer shadow-sm hover:shadow"
                  >
                    <option value="all">All Status</option>
                    <option value="success">Success</option>
                    <option value="failed">Failed</option>
                  </select>
                </div>
                {data.length > 0 && (
                  <div className="text-sm text-gray-600">
                    Total:{" "}
                    <span className="font-semibold text-gray-900">
                      {data.length}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-16">
              <FiLoader
                className="mx-auto text-blue-600 animate-spin"
                size={48}
              />
              <p className="mt-4 text-gray-600">Loading history logs...</p>
            </div>
          ) : error && !data.length ? (
            <div className="text-center py-16">
              <FiAlertCircle className="mx-auto text-red-500" size={48} />
              <p className="mt-4 text-gray-600">{error}</p>
              <button
                onClick={() => fetchHistoryLogs(page)}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Try Again
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  {table.getHeaderGroups().map((hg) => (
                    <tr key={hg.id}>
                      {hg.headers.map((header) => (
                        <th
                          key={header.id}
                          className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider"
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>

                <tbody className="bg-white divide-y divide-gray-200">
                  {data.length === 0 ? (
                    <tr>
                      <td
                        colSpan={columns.length}
                        className="text-center py-12"
                      >
                        <FiFileText
                          className="mx-auto text-gray-400"
                          size={48}
                        />
                        <p className="mt-4 text-gray-600">
                          No history logs found for the selected date range
                        </p>
                        <p className="text-sm text-gray-500 mt-2">
                          Try selecting a different date range
                        </p>
                      </td>
                    </tr>
                  ) : (
                    table.getRowModel().rows.map((row) => (
                      <>
                        <tr
                          key={row.id}
                          className="hover:bg-gray-50 transition-colors duration-150"
  //                          onMouseEnter={() => handleUserHover(row.original.userId)}
  // onMouseLeave={handleUserLeave}
                        >
                          {row.getVisibleCells().map((cell) => (
                            <td
                              key={cell.id}
                              className="px-6 py-4 whitespace-nowrap"
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </td>
                          ))}
                        </tr>

                        {expanded[row.id] && (
                          <tr className="bg-gradient-to-r from-gray-50 to-white">
                            <td colSpan={columns.length} className="px-6 py-6">
                              <ExpandedHistoryLog history={row.original} />
                            </td>
                          </tr>
                        )}
                      </>
                    ))
                  )}
                </tbody>
              </table>
              <div className="flex justify-center mt-4 mb-4">
                <Pagination
                  count={pagination.totalPages || 1}
                  page={page}
                  onChange={(e, v) => fetchHistoryLogs(v)}
                  color="primary"
                  shape="rounded"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    {hoveredUser && (
  <div
    className="fixed z-50 w-[320px]"
    style={{
      top: popupPos.y + 10,
      left: popupPos.x + 10,
    }}
  >
    <div className="bg-white rounded-3xl shadow-2xl border border-slate-200 p-5 transition-all">

      {userLoading ? (
        <p className="text-xs font-bold text-slate-400">Loading...</p>
      ) : userDetails ? (
        <>
          {/* Profile */}
          <div className="flex items-center gap-3 mb-4">
            <img
              src={userDetails.profile?.info?.avatar}
              className="w-12 h-12 rounded-full border"
            />
            <div>
              <p className="text-sm font-black text-slate-800">
                {userDetails.profile?.info?.name}
              </p>
              <p className="text-[10px] text-slate-400">
                {userDetails.profile?.info?.email}
              </p>
            </div>
          </div>

          {/* Subscription */}
          <div className="mb-3">
            <p className="text-[10px] font-black uppercase text-slate-400">Plan</p>
            <p className="text-sm font-bold text-indigo-600">
              {userDetails.subscription?.subscriptionType} ({userDetails.subscription?.status})
            </p>
          </div>

          {/* Devices */}
          <div className="mb-3">
            <p className="text-[10px] font-black uppercase text-slate-400">Devices</p>
            <p className="text-sm font-bold">
              {userDetails.infrastructure?.devices?.total} total •{" "}
              <span className="text-emerald-600">
                {userDetails.infrastructure?.devices?.online} online
              </span>
            </p>
          </div>

          {/* Location */}
          <div>
            <p className="text-[10px] font-black uppercase text-slate-400">Location</p>
            <p className="text-sm font-bold text-slate-700">
              {userDetails.profile?.location?.city},{" "}
              {userDetails.profile?.location?.state}
            </p>
          </div>
        </>
      ) : (
        <p className="text-xs text-slate-400">No data</p>
      )}
    </div>
  </div>
)}
    </div>

    
  );
}

/* ---------------- Expanded History Log Component ---------------- */
function ExpandedHistoryLog({ history }) {
  return (
    <div className="space-y-6 animate-fade-in">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* <InfoCard
          icon={<FiClock className="text-blue-600" size={20} />}
          label="Last Run"
          value={
            history.lastRunAt ? formatDate(history.lastRunAt) : "N/A"
          }
          bgColor="bg-blue-50"
          borderColor="border-blue-200"
        /> */}
        <InfoCard
          icon={<FiCalendar className="text-purple-600" size={20} />}
          label="Calender Type"
          value={
            history.when?.calenderType
              ? history.when.calenderType.charAt(0).toUpperCase() +
              history.when.calenderType.slice(1)
              : "N/A"
          }
          bgColor="bg-purple-50"
          borderColor="border-purple-200"
        />
        {/* <InfoCard
          icon={<FiZap className="text-indigo-600" size={20} />}
          label="Exact Time"
          value={history.when?.exactTime || "N/A"}
          bgColor="bg-indigo-50"
          borderColor="border-indigo-200"
        /> */}
        <InfoCard
          icon={<FiActivity className="text-emerald-600" size={20} />}
          label={history.smartRestoreTriggered ? "Smart Restore" : "Cron Status"}
          value={
            history.cronstatus
              ? history.cronstatus.charAt(0).toUpperCase() +
              history.cronstatus.slice(1)
              : "N/A"
          }
          bgColor="bg-emerald-50"
          borderColor="border-emerald-200"
        />
      </div>

      {/* Devices Section */}
      {history.actions && history.actions.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <FiServer className="text-indigo-600" size={20} />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">
              Devices ({history.actions.length})
            </h3>
          </div>
          <div className="grid sm:grid-cols-1 md:grid-cols-2 gap-4">
            {history.actions.map((d) => (
              <DeviceCard key={d._id || Math.random()} device={d} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- Device Card Component ---------------- */
// function DeviceCard({ device }) {
//   return (
//     <div className="bg-gradient-to-br from-white to-gray-50 border-2 rounded-xl p-5 hover:shadow-lg transition-all duration-200 border-gray-200">
//       <div className="flex items-center justify-between mb-4">
//         <div className="flex items-center gap-3">
//           <div
//             className={`p-2 rounded-lg ${device.deviceOn ? "bg-emerald-100" : "bg-gray-200"
//               }`}
//           >
//             {device.deviceOn ? (
//               <FiPower className="text-emerald-600" size={18} />
//             ) : (
//               <FiPower className="text-gray-600" size={18} />
//             )}
//           </div>
//           <div>
        
//             <span className="font-bold text-gray-900 break-all block max-w-xs leading-snug">
//               {device.deviceAlias || "N/A"}
//             <p className="text-xs text-gray-500">{device.deviceId}</p>
//             </span>
//           </div>
//         </div>
//         <span
//           className={`px-3 py-1 text-xs font-semibold rounded-full ${device.deviceOn
//             ? "bg-emerald-100 text-emerald-700 border border-emerald-300"
//             : "bg-gray-100 text-gray-700 border border-gray-300"
//             }`}
//         >
//           {device.deviceOn ? "ON" : "OFF"}
//         </span>
//       </div>

//       <div className="space-y-2">
//         <div className="flex items-center justify-between text-sm">
//           <span className="text-gray-600 flex items-center gap-2">
//             <FiCheckCircle
//               className={device.supported ? "text-emerald-500" : "text-red-500"}
//               size={14}
//             />
//             Supported
//           </span>
//           <span
//             className={`font-medium ${device.supported ? "text-emerald-700" : "text-red-700"
//               }`}
//           >
//             {device.supported ? "Yes" : "No"}
//           </span>
//         </div>

//         <div className="flex items-center justify-between text-sm">
//           <span className="text-gray-600 flex items-center gap-2">
//             <FiZap
//               className={
//                 device.executed ? "text-emerald-500" : "text-yellow-500"
//               }
//               size={14}
//             />
//             Executed
//           </span>
//           <span
//             className={`font-medium ${device.executed ? "text-emerald-700" : "text-yellow-700"
//               }`}
//           >
//             {device.executed ? "Yes" : "No"}
//           </span>
//         </div>

//         {device.executedAt && (
//           <div className="flex items-center justify-between text-sm">
//             <span className="text-gray-600 flex items-center gap-2">
//               <FiClock size={14} />
//               Executed At
//             </span>
//             <span className="font-medium text-gray-900 text-xs">
//               {formatDate(device.executedAt)}
//             </span>
//           </div>
//         )}

//         {device.brightnessLevel !== null &&
//           device.brightnessLevel !== undefined &&
//           device.brightnessSupported && (
//             <div className="flex items-center justify-between text-sm">
//               <span className="text-gray-600 flex items-center gap-2">
//                 <FiSun size={14} />
//                 Brightness
//               </span>
//               <span className="font-medium text-gray-900">
//                 {device.brightnessLevel}%
//               </span>
//             </div>
//           )}

//         {device.remark && (
//           <div className="flex items-center justify-between text-sm pt-2 border-t border-gray-200">
//             <span className="text-gray-600 flex items-center gap-2">
//               <FiFileText size={14} />
//               Remark
//             </span>
//             <span className="font-medium text-gray-900 text-xs text-right max-w-xs">
//               {device.remark}
//             </span>
//           </div>
//         )}

//         <div className="flex items-center justify-between text-sm pt-2 border-t border-gray-200">
//           <span className="text-gray-600 flex items-center gap-2">
//             <FiRefreshCw size={14} />
//             Allowed Retries
//           </span>
//           <span className="font-medium text-gray-900">
//             {device.allowedRetries || 0}
//           </span>
//         </div>

//         {device.failureCount !== undefined && (
//           <div className="flex items-center justify-between text-sm">
//             <span className="text-gray-600 flex items-center gap-2">
//               <FiXCircle
//                 className={
//                   device.failureCount > 0 ? "text-red-500" : "text-gray-400"
//                 }
//                 size={14}
//               />
//               Failure Count
//             </span>
//             <span
//               className={`font-medium ${device.failureCount > 0 ? "text-red-700" : "text-gray-700"
//                 }`}
//             >
//               {device.failureCount || 0}
//             </span>
//           </div>
//         )}
//       </div>
//     </div>
//   );
// }



function DeviceCard({ device }) {
  const isHoneywell = device?.brand?.toLowerCase() === 'honeywell';
  const mode = device?.mode?.toLowerCase() || "off"; // Normalize mode

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleString([], {
      dateStyle: 'short',
      timeStyle: 'short'
    });
  };

  return (
    <div className="bg-gradient-to-br from-white to-gray-50 border-2 rounded-xl p-5 hover:shadow-lg transition-all duration-200 border-gray-200">
      {/* --- HEADER SECTION --- */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${device.deviceOn ? "bg-emerald-100" : "bg-gray-200"}`}>
            <FiPower className={device.deviceOn ? "text-emerald-600" : "text-gray-600"} size={18} />
          </div>
          <div>
            <span className="font-bold text-gray-900 break-all block max-w-xs leading-snug">
              {device.deviceAlias || "N/A"}
              <div className="flex items-center gap-2 mt-1">
                <span className={`text-[9px] px-2 py-0.5 rounded-md font-black uppercase tracking-wider shadow-sm border ${
                  isHoneywell ? "bg-orange-500 text-white border-orange-400" : "bg-green-600 text-white border-green-500"
                }`}>
                  {isHoneywell ? "Honeywell" : "Tapo"}
                </span>
                <p className="text-[10px] text-gray-400 font-mono font-medium">{device.deviceId}</p>
              </div>
            </span>
          </div>
        </div>
        <span className={`px-3 py-1 text-xs font-semibold rounded-full ${device.deviceOn ? "bg-emerald-100 text-emerald-700 border border-emerald-300" : "bg-gray-100 text-gray-700 border border-gray-300"}`}>
          {device.deviceOn ? "ON" : "OFF"}
        </span>
      </div>

      <div className="space-y-2">
        {/* Core Status Rows */}
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600 flex items-center gap-2"><FiCheckCircle className={device.supported ? "text-emerald-500" : "text-red-500"} size={14} />Supported</span>
          <span className={`font-medium ${device.supported ? "text-emerald-700" : "text-red-700"}`}>{device.supported ? "Yes" : "No"}</span>
        </div>

        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600 flex items-center gap-2"><FiZap className={device.executed ? "text-emerald-500" : "text-yellow-500"} size={14} />Executed</span>
          <span className={`font-medium ${device.executed ? "text-emerald-700" : "text-yellow-700"}`}>{device.executed ? "Yes" : "No"}</span>
        </div>

        {/* --- HONEYWELL CONDITIONAL LOGIC --- */}
        {isHoneywell && (
          <div className="space-y-2 border-y border-gray-100 py-2 my-2 bg-gray-50/50 px-2 rounded-lg">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600 flex items-center gap-2"><FaStar size={14} className="text-amber-500" />Mode</span>
              <span className="font-bold text-gray-900 text-xs uppercase">{device.mode || "Off"}</span>
            </div>
            
            {/* Show Cool Setpoint: Only if mode is 'cool' or 'auto' */}
            {(mode === "cool" || mode === "auto") && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600 flex items-center gap-2"><FiThermometer size={14} className="text-blue-500"/>Cool Set</span>
                <span className="font-bold text-blue-700">{device.coolSetpoint || 0}°</span>
              </div>
            )}

            {/* Show Heat Setpoint: Only if mode is 'heat' or 'auto' */}
            {(mode === "heat" || mode === "auto") && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600 flex items-center gap-2"><FiThermometer size={14} className="text-orange-500"/>Heat Set</span>
                <span className="font-bold text-orange-700">{device.heatSetpoint || 0}°</span>
              </div>
            )}

            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600 flex items-center gap-2"><FiWind size={14} />Fan Mode</span>
              <span className="font-medium text-gray-900 text-xs uppercase">{device.fanMode || "Auto"}</span>
            </div>
          </div>
        )}

        {/* --- TAPO SPECIFIC (Non-Honeywell) --- */}
        {!isHoneywell && device.brightnessSupported && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600 flex items-center gap-2"><FiSun size={14} />Brightness</span>
            <span className="font-medium text-gray-900">{device.brightnessLevel ?? 0}%</span>
          </div>
        )}

        {/* Footer info (Remark, Retries, Failures) matches your previous code exactly */}
        {device.remark && (
          <div className="flex items-center justify-between text-sm pt-2 border-t border-gray-200">
            <span className="text-gray-600 flex items-center gap-2"><FiFileText size={14} />Remark</span>
            <span className="font-medium text-gray-900 text-xs text-right max-w-xs italic">{device.remark}</span>
          </div>
        )}

        <div className="flex items-center justify-between text-sm pt-2 border-t border-gray-200">
          <span className="text-gray-600 flex items-center gap-2"><FiRefreshCw size={14} />Allowed Retries</span>
          <span className="font-medium text-gray-900">{device.allowedRetries || 0}</span>
        </div>

        {device.failureCount !== undefined && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600 flex items-center gap-2">
              <FiXCircle className={device.failureCount > 0 ? "text-red-500" : "text-gray-400"} size={14} />
              Failure Count
            </span>
            <span className={`font-medium ${device.failureCount > 0 ? "text-red-700" : "text-gray-700"}`}>{device.failureCount || 0}</span>
          </div>
        )}
      </div>
    </div>
  );
}


/* ---------------- Info Card Component ---------------- */
function InfoCard({ icon, label, value, bgColor, borderColor }) {
  return (
    <div className={`${bgColor} ${borderColor} border-2 rounded-xl p-4`}>
      <div className="flex items-center gap-3 mb-2">
        {icon}
        <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">
          {label}
        </p>
      </div>
      <p className="text-lg font-bold text-gray-900">{value}</p>
    </div>
  );
}

/* ---------------- Custom Date Range Picker Component ---------------- */
function CustomDateRangePicker({
  startDate,
  endDate,
  currentMonth,
  onStartDateChange,
  onEndDateChange,
  onMonthChange,
  onClose,
  onApply,
}) {
  const handleDateClick = (date) => {
    if (!date) return;
    const dateStr = getDateString(date);

    if (!startDate || (startDate && endDate)) {
      onStartDateChange(dateStr);
      onEndDateChange("");
      return;
    }

    if (dateStr < startDate) {
      onEndDateChange(startDate);
      onStartDateChange(dateStr);
    } else {
      onEndDateChange(dateStr);
    }
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();

  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startingDayOfWeek = firstDay.getDay();

  const days = useMemo(() => {
    const dayArray = [];
    for (let i = 0; i < startingDayOfWeek; i++) {
      dayArray.push(null);
    }
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day, 0, 0, 0, 0);
      dayArray.push(date);
    }
    return dayArray;
  }, [year, month, startingDayOfWeek, daysInMonth]);

  const getDateString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const isDateInRange = (date) => {
    if (!date || !startDate || !endDate) return false;
    const dateStr = getDateString(date);
    return dateStr >= startDate && dateStr <= endDate;
  };

  const isStartDate = (date) => {
    if (!date || !startDate) return false;
    return getDateString(date) === startDate;
  };

  const isEndDate = (date) => {
    if (!date || !endDate) return false;
    return getDateString(date) === endDate;
  };

  const changeMonth = (direction) => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(currentMonth.getMonth() + direction);
    if (
      newMonth.getMonth() !==
      (((currentMonth.getMonth() + direction) % 12) + 12) % 12
    ) {
      newMonth.setDate(0);
    }
    onMonthChange(newMonth);
  };

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const daysDiff =
    startDate && endDate
      ? Math.floor((new Date(endDate) - new Date(startDate)) / 86400000) + 1
      : 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h3 className="text-xl font-bold text-gray-900">
              Select Date Range
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Choose start and end dates (max 31 days range)
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <FiX size={24} className="text-gray-600" />
          </button>
        </div>

        {/* Calendar */}
        <div className="p-6">
          {/* Month Navigation */}
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => changeMonth(-1)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <FiChevronLeft size={20} className="text-gray-600" />
            </button>
            <h4 className="text-lg font-semibold text-gray-900">
              {monthNames[month]} {year}
            </h4>
            <button
              onClick={() => changeMonth(1)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <FiChevronRight size={20} className="text-gray-600" />
            </button>
          </div>

          {/* Day Names */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {dayNames.map((day) => (
              <div
                key={day}
                className="text-center text-xs font-semibold text-gray-600 py-2"
              >
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1">
            {days.map((date, index) => {
              if (!date) {
                return <div key={`empty-${index}`} className="aspect-square" />;
              }

              const dateStr = getDateString(date);
              const isInRange = isDateInRange(date);
              const isStart = isStartDate(date);
              const isEnd = isEndDate(date);
              const todayStr = getDateString(today);
              const isToday = dateStr === todayStr;

              return (
                <button
                  key={`${dateStr}-${index}`}
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleDateClick(date);
                  }}
                  className={`
                    aspect-square rounded-lg text-sm font-medium transition-all duration-200
                    hover:bg-blue-100 cursor-pointer active:scale-95
                    ${isToday ? "ring-2 ring-blue-500" : ""}
                    ${isStart || isEnd
                      ? "bg-blue-600 text-white hover:bg-blue-700"
                      : isInRange
                        ? "bg-blue-100 text-blue-900"
                        : "hover:bg-gray-100 text-gray-900"
                    }
                    ${date > today
                      ? "opacity-30 cursor-not-allowed hover:bg-transparent text-gray-400"
                      : ""
                    }
                  `}
                  disabled={date > today}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>

          {/* Selected Range Info */}
          {startDate && endDate && (
            <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Selected Range</p>
                  <p className="font-semibold text-gray-900 mt-1">
                    {new Date(startDate).toLocaleDateString("en-US", {
                      month: "long",
                      day: "numeric",
                      year: "numeric",
                    })}{" "}
                    -{" "}
                    {new Date(endDate).toLocaleDateString("en-US", {
                      month: "long",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </p>
                  <p className="text-xs text-gray-600 mt-1">
                    {daysDiff} day{daysDiff !== 1 ? "s" : ""}
                  </p>
                </div>
                {daysDiff > 31 && (
                  <div className="text-red-600 text-sm">
                    <FiAlertCircle size={20} />
                    <p className="mt-1">Max: 31 days</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-3 mt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              onClick={onApply}
              disabled={!startDate || !endDate || daysDiff > 31}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium"
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Helper Functions ---------------- */
function formatDate(date) {
  if (!date) return "N/A";
  try {
    return new Date(date).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return "Invalid Date";
  }
}